/**
 * Whether a contract's commission is being tracked, in a sentence.
 *
 * The nightly ledger sync works out, per record, exactly why it books or
 * does not — nothing to price from, no agent named, the agency has no split,
 * more than one row already — and used to file all of it in a job log. JB
 * Sports ran with 26 priced contracts at 3% and an empty ledger because one
 * settings row did not exist, and no screen said so.
 *
 * The job now writes its conclusion to `commission_status` on the contract
 * or deal. This turns it into something an agent can read. Pure — no
 * network, no PocketBase. Sibling of `otcCoverage.ts`, deliberately.
 */

export type CommissionOutcome =
  | 'booked' | 'repriced' | 'unchanged' | 'nothing_earned'
  | 'unpriced' | 'no_agent' | 'no_split' | 'duplicate' | 'failed'

export interface CommissionStatusRecord {
  outcome: CommissionOutcome
  reason: string | null
  checked_at: string
}

export interface CommissionCoverage {
  /** 'tracked' money is booked · 'waiting' nothing to book yet · 'blocked' something must be fixed */
  tone: 'tracked' | 'waiting' | 'blocked'
  message: string
  /**
   * True when the cause is the agency's, not this record's — every contract
   * in the agency is stopped by the same thing, and it is fixed in one place.
   */
  agencyWide: boolean
  checkedAt: string | null
}

function read(value: unknown): CommissionStatusRecord | null {
  if (!value || typeof value !== 'object') return null
  const v = value as Record<string, any>
  if (typeof v.outcome !== 'string') return null
  return {
    outcome: v.outcome as CommissionOutcome,
    reason: typeof v.reason === 'string' ? v.reason : null,
    checked_at: typeof v.checked_at === 'string' ? v.checked_at : '',
  }
}

/**
 * A line for a contract or deal, or null when there is nothing worth saying
 * — including before the job has ever looked at it, and for a record with no
 * commission rate, where silence is the right answer rather than a
 * complaint.
 */
export function commissionCoverage(record: {
  commission_rate?: number | null
  commission_status?: unknown
}): CommissionCoverage | null {
  const status = read(record.commission_status)
  if (!status) return null

  const checkedAt = status.checked_at || null
  const because = status.reason ? ` — ${status.reason}` : ''

  switch (status.outcome) {
    case 'booked':
    case 'repriced':
    case 'unchanged':
      return {
        tone: 'tracked',
        message: `Commission is booked to the ledger${because}.`,
        agencyWide: false,
        checkedAt,
      }
    case 'nothing_earned':
      return {
        tone: 'waiting',
        message: `Nothing has been earned on this yet, so nothing is booked${because}.`,
        agencyWide: false,
        checkedAt,
      }
    case 'unpriced':
      return {
        tone: 'blocked',
        message: `Commission cannot be worked out yet${because}. Add the season figures, or the guaranteed value, and it will book on the next run.`,
        agencyWide: false,
        checkedAt,
      }
    case 'no_agent':
      return {
        tone: 'blocked',
        message: `No commission is booked${because}. Set the owning agent on this record.`,
        agencyWide: false,
        checkedAt,
      }
    case 'no_split':
      return {
        tone: 'blocked',
        message: 'No commission is being tracked anywhere for this agency: no split is on file, so there is no way to divide what is earned. Set it once on the Commission page and every contract books on the next run.',
        agencyWide: true,
        checkedAt,
      }
    case 'duplicate':
      return {
        tone: 'blocked',
        message: `More than one commission row exists for this record${because}. The extra rows need removing before the figure can be trusted.`,
        agencyWide: false,
        checkedAt,
      }
    case 'failed':
      return {
        tone: 'blocked',
        message: `The last attempt to book this commission failed${because}.`,
        agencyWide: false,
        checkedAt,
      }
    default:
      return null
  }
}

/**
 * The one agency-wide blocker, from whatever set of records is to hand — for
 * a page that shows totals rather than one contract. Null when nothing says
 * so, which includes the case where the job has not run yet.
 */
export function agencyCommissionBlocker(records: { commission_status?: unknown }[]): CommissionCoverage | null {
  for (const record of records) {
    const coverage = commissionCoverage(record)
    if (coverage?.agencyWide) return coverage
  }
  return null
}
